import { useEffect, useRef } from 'react'
import { connect } from 'react-redux'
import * as maptilersdk from '@maptiler/sdk'
import { removeUpdatedFlag, markerDeleteRequest } from '../../../action/marker-actions'
import { store } from '../../../lib/store'
import { expandLeftToolbar, collapseLeftToolbar } from '../../../action/left-toolbar-state-actions'
import { expandRightToolbar } from '../../../action/right-toolbar-state-actions'
import { photoDeleteRequest } from '../../../action/photo-actions'
import { dateConvertion } from '../../main-ui/common/commonfunctions'
import buildPopupContent from './buildPopupContent'
import { buildPhotoMarkerElement } from '../render-3d-photos'

const SRC             = 'bm-pins-src'
const CLUSTER_CIRCLE  = 'bm-pins-cluster'
const CLUSTER_COUNT   = 'bm-pins-cluster-count'
const UNCLUSTERED_PROBE = 'bm-pins-probe'   // invisible, queryable for unclustered IDs

const FALLBACK_ICON = 'https://basemap-core.s3.us-west-2.amazonaws.com/markertypes/svg/Location.svg'

function normaliseColor(color) {
  if (!color) return '#9ecc3b'
  let c = color.startsWith('#') ? color : '#' + color
  if (c.length > 7) c = '#' + c.slice(-6)
  return c
}

function buildMarkerElement(color, vectorIconUrl) {
  const colorValue = normaliseColor(color)
  const clipId = `cc-${Math.random().toString(36).slice(2, 7)}`
  const iconUrl = (vectorIconUrl || '').replace(/\s/g, '%20') || FALLBACK_ICON

  const el = document.createElement('div')
  el.style.cursor = 'pointer'
  el.innerHTML = `
    <svg viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg"
      xmlns:xlink="http://www.w3.org/1999/xlink" width="35" height="35">
      <defs>
        <clipPath id="${clipId}">
          <circle cx="8" cy="6" r="5"/>
        </clipPath>
      </defs>
      <path d="M8,0C4.688,0,2,2.688,2,6c0,6,6,10,6,10s6-4,6-10C14,2.688,11.312,0,8,0z
               M8,8C6.344,8,5,6.656,5,5s1.344-3,3-3s3,1.344,3,3S9.656,8,8,8z"
            fill="${colorValue}"/>
      <circle cx="8" cy="6" r="5" fill="#FFFFFF"/>
      <image href="${iconUrl}" xlink:href="${iconUrl}"
             x="4" y="1.5" width="8" height="8" clip-path="url(#${clipId})"/>
    </svg>`
  return el
}

function buildPhotoPopupContent(photo, onEdit, onShare, onDelete, onImageClick, popup) {
  const { userPreferences } = store.getState()
  const formattedDate = dateConvertion(photo.created)
  const alt = userPreferences?.units === 'imperial'
    ? ((photo.altitude ?? 0) * 3.28084).toLocaleString('en-IN', { maximumFractionDigits: 0 }) + ' ft'
    : (photo.altitude ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 }) + ' m'

  const wrap = document.createElement('div')
  wrap.style.cssText = 'padding:10px;height:165px;width:260px;background-color:black;visibility:visible;border-radius:10px;position:relative'
  wrap.innerHTML = `
    <div class="info-window-header-container">
      <div style="width:90%;overflow:hidden;white-space:nowrap;text-overflow:ellipsis;font-weight:bold;font-size:13px;color:#fff">${photo.name ?? ''}</div>
      <div class="info-window-closeicon">
        <a id="iw-close" style="cursor:pointer">
          <img style="width:20px;object-fit:cover;float:right;margin-top:-10px" src="/asset/close.png" alt="Close"/>
        </a>
      </div>
    </div>
    <div style="width:100%;display:flex;position:relative;z-index:111">
      <div style="width:55%">
        <h6 class="info-box-head-date">Created: <span class="info-box-head-date">${formattedDate ?? 'N/A'}</span></h6>
        <h6 class="info-box-head">Elev: <span class="info-box-span">${alt}</span></h6>
        <h6 class="info-box-head">Latitude: <span class="info-box-span">${Number(photo.latitude).toFixed(5)}</span></h6>
        <h6 class="info-box-head">Longitude: <span class="info-box-span">${Number(photo.longitude).toFixed(5)}</span></h6>
        <div style="display:flex;margin-top:10px;margin-right:10px">
          <div style="width:50px;height:55px;display:flex;flex-direction:column;align-items:center;justify-content:center">
            <a id="iw-share" style="cursor:pointer"><div class="info-window-bottons"><img style="width:50%;height:50%;object-fit:cover" src="/asset/shared.png"/></div></a>
            <p>Share</p>
          </div>
          <div style="width:50px;height:55px;display:flex;flex-direction:column;align-items:center;justify-content:center">
            <a id="iw-edit" style="cursor:pointer"><div class="info-window-bottons"><img style="width:50%;height:50%;object-fit:cover" src="/asset/pencil.png" alt="Edit"/></div></a>
            <p>Edit</p>
          </div>
          <div style="width:50px;height:55px;display:flex;flex-direction:column;align-items:center;justify-content:center">
            <a id="iw-delete" style="cursor:pointer"><div class="info-window-bottons"><img style="width:50%;height:50%;object-fit:cover" src="/asset/bin.png"/></div></a>
            <p>Delete</p>
          </div>
        </div>
      </div>
      <div style="width:115px;height:125px;margin-top:10px;display:flex;justify-content:center;align-items:normal">
        <img id="iw-image" src="${photo.thumbnailUrl || photo.imageUrl || ''}" alt="${photo.name ?? ''}"
          style="width:100%;height:100%;object-fit:cover;cursor:pointer;border-radius:4px;border:1px solid #444"/>
      </div>
    </div>`

  wrap.querySelector('#iw-close').addEventListener('click', e => { e.stopPropagation(); popup.remove() })
  wrap.querySelector('#iw-edit').addEventListener('click', e => { e.stopPropagation(); onEdit() })
  wrap.querySelector('#iw-share').addEventListener('click', e => { e.stopPropagation(); onShare() })
  wrap.querySelector('#iw-delete').addEventListener('click', e => { e.stopPropagation(); onDelete() })
  wrap.querySelector('#iw-image').addEventListener('click', e => { e.stopPropagation(); onImageClick() })
  return wrap
}

let imageOverlay = null
function showImageOverlay(photo) {
  if (imageOverlay) imageOverlay.remove()
  const container = document.createElement('div')
  container.style.cssText = 'position:fixed;inset:0;z-index:999999;background:rgba(0,0,0,0.85);display:flex;align-items:center;justify-content:center'
  container.innerHTML = `
    <div style="position:relative;max-width:90vw;max-height:90vh;text-align:center">
      <img src="${photo.imageUrl}" style="max-width:100%;max-height:80vh;border:2px solid #589500;border-radius:4px"/>
      <p style="color:#fff;margin-top:8px;font-size:14px">${photo.name ?? ''}</p>
      <button style="position:absolute;top:-12px;right:-12px;background:#333;border:none;color:#fff;border-radius:50%;width:28px;height:28px;cursor:pointer;font-size:16px">&times;</button>
    </div>`
  container.querySelector('button').addEventListener('click', () => container.remove())
  container.addEventListener('click', e => { if (e.target === container) container.remove() })
  document.body.appendChild(container)
  imageOverlay = container
}

function buildFC(markers, photos) {
  const features = []
  for (const m of markers) {
    if (m.latitude == null || m.longitude == null) continue
    features.push({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [m.longitude, m.latitude] },
      properties: { _id: m.id, _type: 'marker', _name: m.name ?? '' },
    })
  }
  for (const p of photos) {
    if (p.latitude == null || p.longitude == null) continue
    features.push({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [p.longitude, p.latitude] },
      properties: { _id: p.id, _type: 'photo', _name: p.name ?? '' },
    })
  }
  return { type: 'FeatureCollection', features }
}

function createDomPin(map, obj, type) {
  let el, popup

  if (type === 'photo') {
    el = buildPhotoMarkerElement(obj.color, obj.thumbnailUrl || obj.imageUrl)
    popup = new maptilersdk.Popup({ closeButton: false, maxWidth: '340px', className: 'bm-marker-popup', offset: 25 })
    const photoWithType = { ...obj, measureType: 'photo' }
    const onEdit = () => {
      popup.remove()
      setTimeout(() => store.dispatch(expandLeftToolbar({ dialog: 'edit', editable: photoWithType, mapObj: pin })), 200)
    }
    const onShare = () => {
      const domain = process.env.NODE_ENV === 'production' ? 'app.basemap.com' : 'staging.basemap.com'
      const body = `I've shared a photo with you. https://${domain}/marker?latitude=${obj.latitude}&longitude=${obj.longitude}&n=${obj.name}`
      window.location.href = `mailto:?subject=${encodeURIComponent('BaseMap Shared Photo')}&body=${encodeURIComponent(body)}`
    }
    const onDelete = () => { popup.remove(); store.dispatch(photoDeleteRequest(obj.id)) }
    // Build popup content only when the popup actually opens — building it eagerly
    // for every visible pin would construct the DOM (and fetch the full-res photo
    // in the popup <img>) for photos that are never clicked.
    popup.on('open', () => popup.setDOMContent(buildPhotoPopupContent(obj, onEdit, onShare, onDelete, () => showImageOverlay(obj), popup)))
  } else {
    el = buildMarkerElement(obj.color, obj.markerType?.vectorIconUrl)
    const tooltip = new maptilersdk.Popup({ closeButton: false, closeOnClick: false, offset: 50, className: 'bm-name-tooltip', focusAfterOpen: false })
    tooltip.setHTML(`<span>${obj.name ?? ''}</span>`)
    el.addEventListener('mouseenter', () => tooltip.setLngLat([obj.longitude, obj.latitude]).addTo(map))
    el.addEventListener('mouseleave', () => tooltip.remove())

    popup = new maptilersdk.Popup({ closeButton: false, maxWidth: '320px', className: 'bm-marker-popup', offset: 25 })
    const onEdit = () => {
      popup.remove()
      store.dispatch(collapseLeftToolbar())
      store.dispatch(expandLeftToolbar({ dialog: 'edit', editable: { ...obj, measureType: 'marker' }, mapObj: pin }))
    }
    const onShare = () => {
      const domain = process.env.NODE_ENV === 'production' ? 'app.basemap.com' : 'staging.basemap.com'
      const body = `I've shared a marker with you. https://${domain}/marker?latitude=${obj.latitude}&longitude=${obj.longitude}&t=63&n=${obj.name}`
      window.location.href = `mailto:?subject=${encodeURIComponent('BaseMap Shared Marker')}&body=${encodeURIComponent(body)}`
    }
    const onDelete = () => { popup.remove(); store.dispatch(markerDeleteRequest(obj.id)) }
    const onHuntWind = () => { popup.remove(); store.dispatch(expandRightToolbar({ dialog: 'huntWind', data: obj })) }
    // Build popup content only when the popup actually opens (see photo branch).
    popup.on('open', () => popup.setDOMContent(buildPopupContent(obj, onEdit, onShare, onDelete, onHuntWind, popup)))
  }

  const pin = new maptilersdk.Marker({ element: el, anchor: 'bottom', pitchAlignment: 'viewport', rotationAlignment: 'viewport' })
    .setLngLat([obj.longitude, obj.latitude])
    .setPopup(popup)
    .addTo(map)
  return pin
}

function teardown(m, instancesRef) {
  if (!m) return
  Object.values(instancesRef.current).forEach(pin => pin.remove())
  instancesRef.current = {}
  ;[UNCLUSTERED_PROBE, CLUSTER_COUNT, CLUSTER_CIRCLE].forEach(id => {
    if (m.getLayer(id)) m.removeLayer(id)
  })
  if (m.getSource(SRC)) m.removeSource(SRC)
}

const RenderMarkers = ({ map, markers, photos, leftToolbarState, removeUpdatedFlag }) => {
  const instancesRef   = useRef({})
  const markersRef     = useRef([])
  const photosRef      = useRef([])
  // Keyed lookup maps rebuilt on renders (infrequent) so sync can do O(1) lookups.
  const markerByKeyRef = useRef(new Map())
  const photoByKeyRef  = useRef(new Map())
  const allIdsRef      = useRef(new Set())
  const activeMapRef   = useRef(null)
  const syncRef        = useRef(null)
  const editingRef     = useRef({ markerId: null, photoId: null })

  markersRef.current = markers
  photosRef.current  = photos
  // O(N) rebuild happens here (on renders, ~once per poll cycle) not in the sync
  // callback (which fires on every map idle event during pan/zoom/cluster settle).
  markerByKeyRef.current = new Map(markers.map(mk => [`marker:${mk.id}`, mk]))
  photoByKeyRef.current  = new Map(photos.map(p  => [`photo:${p.id}`,   p]))
  allIdsRef.current = new Set([...markerByKeyRef.current.keys(), ...photoByKeyRef.current.keys()])

  editingRef.current = {
    markerId: leftToolbarState?.dialog === 'edit' && leftToolbarState.editable?.measureType === 'marker'
      ? String(leftToolbarState.editable.id) : null,
    photoId: leftToolbarState?.dialog === 'edit' && leftToolbarState.editable?.measureType === 'photo'
      ? String(leftToolbarState.editable.id) : null,
  }

  syncRef.current = () => {
    const m = activeMapRef.current
    if (!m?.getLayer(UNCLUSTERED_PROBE)) return

    const unclusteredFeats = m.queryRenderedFeatures({ layers: [UNCLUSTERED_PROBE] })
    const visibleIds = new Set(unclusteredFeats.map(f => `${f.properties._type}:${f.properties._id}`))

    const allIds = allIdsRef.current
    const { markerId: eMarkerId, photoId: ePhotoId } = editingRef.current

    // Remove pins no longer in data or no longer visible — iterates only active
    // DOM pins (bounded by N_visible), not the full item list.
    for (const key of Object.keys(instancesRef.current)) {
      const [type, id] = key.split(':')
      const isEditing = (type === 'marker' && id === eMarkerId) || (type === 'photo' && id === ePhotoId)
      if (!allIds.has(key) || !visibleIds.has(key) || isEditing) {
        instancesRef.current[key].remove()
        delete instancesRef.current[key]
      }
    }

    // Iterate only the visible unclustered set — O(N_visible) instead of O(N_total).
    // At typical zoom levels with clustering active, N_visible << N_total.
    for (const key of visibleIds) {
      const [type, id] = key.split(':')
      const isEditing = (type === 'marker' && id === eMarkerId) || (type === 'photo' && id === ePhotoId)
      if (isEditing) continue

      const obj = type === 'marker' ? markerByKeyRef.current.get(key) : photoByKeyRef.current.get(key)
      if (!obj) continue

      if (instancesRef.current[key] && !obj.isUpdated) continue

      if (instancesRef.current[key]) {
        instancesRef.current[key].remove()
        delete instancesRef.current[key]
      }
      instancesRef.current[key] = createDomPin(m, obj, type)
    }
  }

  // Cluster interactions and the visibility sync bind to the map instance, not to the
  // source. Registering them alongside addSource stacked a duplicate set every time a
  // style reload wiped the layers and they were re-added.
  useEffect(() => {
    if (!map) return

    const onIdle = () => syncRef.current()
    const onClusterClick = (e) => {
      const feat = e.features?.[0]
      if (!feat) return
      map.getSource(SRC)?.getClusterExpansionZoom(feat.properties.cluster_id, (err, zoom) => {
        if (err) return
        map.flyTo({ center: feat.geometry.coordinates, zoom: zoom + 1 })
      })
    }
    const onClusterEnter = () => { map.getCanvas().style.cursor = 'pointer' }
    const onClusterLeave = () => { map.getCanvas().style.cursor = '' }

    map.on('idle', onIdle)
    map.on('click', CLUSTER_CIRCLE, onClusterClick)
    map.on('mouseenter', CLUSTER_CIRCLE, onClusterEnter)
    map.on('mouseleave', CLUSTER_CIRCLE, onClusterLeave)

    return () => {
      map.off('idle', onIdle)
      map.off('click', CLUSTER_CIRCLE, onClusterClick)
      map.off('mouseenter', CLUSTER_CIRCLE, onClusterEnter)
      map.off('mouseleave', CLUSTER_CIRCLE, onClusterLeave)
    }
  }, [map])

  useEffect(() => {
    if (!map) return

    if (activeMapRef.current && activeMapRef.current !== map) {
      teardown(activeMapRef.current, instancesRef)
    }
    activeMapRef.current = map

    const apply = () => {
      const fc = buildFC(markersRef.current, photosRef.current)

      if (!map.getSource(SRC)) {
        map.addSource(SRC, {
          type: 'geojson',
          data: fc,
          cluster: true,
          clusterMaxZoom: 14,
          clusterRadius: 50,
        })

        map.addLayer({
          id: CLUSTER_CIRCLE,
          type: 'circle',
          source: SRC,
          filter: ['has', 'point_count'],
          paint: {
            'circle-color': ['step', ['get', 'point_count'], '#589500', 10, '#3d6600', 50, '#1f3300'],
            'circle-radius': ['step', ['get', 'point_count'], 18, 10, 24, 50, 30],
            'circle-opacity': 0.88,
            'circle-stroke-width': 2,
            'circle-stroke-color': '#fff',
            'circle-stroke-opacity': 0.6,
          },
        })

        map.addLayer({
          id: CLUSTER_COUNT,
          type: 'symbol',
          source: SRC,
          filter: ['has', 'point_count'],
          layout: {
            'text-field': ['get', 'point_count_abbreviated'],
            'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
            'text-size': 13,
            'text-allow-overlap': true,
          },
          paint: { 'text-color': '#fff' },
        })

        map.addLayer({
          id: UNCLUSTERED_PROBE,
          type: 'circle',
          source: SRC,
          filter: ['!', ['has', 'point_count']],
          paint: { 'circle-radius': 1, 'circle-opacity': 0.01 },
        })
      } else {
        map.getSource(SRC).setData(fc)
      }

      const current = markersRef.current
      if (current.some(m => m.isUpdated)) removeUpdatedFlag(current)
    }

    // setStyle() destroys every custom source and layer, and addSource throws while a
    // style is still loading. styleKey remounts this component after a basemap switch,
    // but style.load can fire again afterwards (setTerrain triggers a reload), so re-add
    // on every style load too — apply() is idempotent via the getSource(SRC) guard.
    // Without this the probe layer stays missing and the idle sync bails before it can
    // create a single pin.
    const onStyleLoad = () => {
      map.off('idle', apply)
      map.once('idle', apply)
    }
    map.on('style.load', onStyleLoad)

    if (map.isStyleLoaded()) {
      apply()
    } else {
      map.once('idle', apply)
    }

    return () => {
      map.off('style.load', onStyleLoad)
      map.off('idle', apply)
    }
  }, [map, markers, photos])

  useEffect(() => {
    syncRef.current?.()
  }, [leftToolbarState])

  useEffect(() => {
    return () => teardown(activeMapRef.current, instancesRef)
  }, [])

  return null
}

const mapStateToProps = state => ({
  markers: state.markers,
  photos: state.photos,
  leftToolbarState: state.leftToolbarState,
})
const mapDispatchToProps = dispatch => ({
  removeUpdatedFlag: payload => dispatch(removeUpdatedFlag(payload)),
})

export default connect(mapStateToProps, mapDispatchToProps)(RenderMarkers)
